import React from 'react';
import { Link } from 'react-router-dom';
import { useTheme } from '../contexts/ThemeContext';
import { Ruler, Scale, Box, CheckCircle } from 'lucide-react';

const MeasuringGuide = () => {
  const { themeColors } = useTheme();

  const container = {
    minHeight: 'calc(100vh - 120px)',
    backgroundColor: themeColors.background,
    padding: '40px 20px',
  };
  const content = { maxWidth: '820px', margin: '0 auto' };
  const card = {
    backgroundColor: themeColors.cardBackground || '#fff',
    border: `1px solid ${themeColors.border}`,
    borderRadius: '12px',
    padding: '24px',
    marginBottom: '20px',
  };
  const h2 = { color: themeColors.text, fontSize: '20px', margin: '0 0 8px 0', display: 'flex', alignItems: 'center', gap: '10px' };
  const p = { color: themeColors.textSecondary, fontSize: '15px', lineHeight: 1.6, margin: '6px 0' };

  const dims = [
    {
      label: 'Length',
      desc: 'The longest side of the item, measured straight across at its widest point.',
    },
    {
      label: 'Width',
      desc: 'The shorter side — measured straight across from front to back at its widest point.',
    },
    {
      label: 'Height',
      desc: 'How tall the item stands, from the bottom to the top while it sits upright.',
    },
  ];

  return (
    <div style={container}>
      <div style={content}>
        <div style={{ textAlign: 'center', marginBottom: '32px' }}>
          <h1 style={{ color: themeColors.text, fontSize: '36px', fontWeight: 'bold', marginBottom: '10px' }}>
            How to Measure Your Item
          </h1>
          <p style={{ ...p, fontSize: '17px', maxWidth: '620px', margin: '0 auto' }}>
            Measure the <strong>item itself</strong> — not a box. We add packing space automatically and
            tell you exactly which box to use, so buyers get an accurate shipping price.
          </p>
        </div>

        <div style={card}>
          <h2 style={h2}><Ruler size={22} color={themeColors.primary} /> The three measurements</h2>
          {dims.map((d) => (
            <div key={d.label} style={{ marginTop: '14px' }}>
              <strong style={{ color: themeColors.text }}>{d.label}</strong>
              <p style={p}>{d.desc}</p>
            </div>
          ))}
          <p style={{ ...p, marginTop: '14px' }}>
            Tip: use a tape measure or ruler in <strong>inches</strong>, and always round <strong>up</strong> to
            the nearest inch. The order of the three numbers doesn't matter — just measure each side at its widest.
          </p>
        </div>

        <div style={card}>
          <h2 style={h2}><Scale size={22} color={themeColors.primary} /> Weight</h2>
          <p style={p}>
            Weigh the item on a kitchen or bathroom scale and round up to the nearest half pound. Don't worry
            about the box or packing material — we add that for you automatically.
          </p>
          <p style={p}>
            No scale? A good estimate is fine. You can always edit the listing later if it's off.
          </p>
        </div>

        <div style={card}>
          <h2 style={h2}><Box size={22} color={themeColors.primary} /> Measuring odd shapes</h2>
          <p style={p}>
            For irregular items (toys, strollers, anything not box-shaped), imagine the smallest box the item
            would fit inside, and measure that box's length, width, and height. Measure the item at its
            <strong> widest, deepest, and tallest</strong> points.
          </p>
          <p style={p}>
            For soft items like clothing or blankets, lay them flat and fold them the way you'd pack them, then
            measure the folded stack.
          </p>
        </div>

        <div style={{ ...card, background: themeColors.background, border: `1px dashed ${themeColors.border}` }}>
          <h2 style={h2}><CheckCircle size={22} color={themeColors.primary} /> What happens next</h2>
          <p style={p}>
            Once you enter the item's size, we suggest the best box to ship it in (often a free USPS Flat Rate
            box), show buyers an accurate shipping price, and include the box recommendation in your sale email
            and seller dashboard — so you'll know exactly how to pack and mail it.
          </p>
        </div>

        <div style={{ textAlign: 'center', marginTop: '24px' }}>
          <Link
            to="/create-listing"
            style={{
              display: 'inline-block',
              padding: '12px 24px',
              background: themeColors.primary,
              color: '#fff',
              borderRadius: '8px',
              textDecoration: 'none',
              fontWeight: 600,
            }}
          >
            Back to creating your listing
          </Link>
        </div>
      </div>
    </div>
  );
};

export default MeasuringGuide;
